import { Injectable } from '@angular/core';
import { Observable, defer, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

interface CacheEntry {
  value: unknown;
  expiresAt: number;
}

/**
 * Small, deliberate cache for reference data (categories, banners, ...).
 *
 * - `ttlMs` bounds staleness, `MAX_ENTRIES` bounds memory (oldest evicted first).
 * - Identical requests made while one is in flight share a single HTTP call,
 *   which removes the duplicate category fetches several pages fired on init.
 * - Errors are never cached, so the next caller retries.
 *
 * Do NOT route prices, stock, cart, order or delivery data through this - those
 * must always come fresh from the server.
 */
@Injectable({ providedIn: 'root' })
export class RequestCacheService {
  private static readonly MAX_ENTRIES = 50;
  private readonly entries = new Map<string, CacheEntry>();
  private readonly inFlight = new Map<string, Observable<unknown>>();

  get<T>(key: string, ttlMs: number, factory: () => Observable<T>): Observable<T> {
    return defer(() => {
      const hit = this.entries.get(key);
      if (hit && hit.expiresAt > Date.now()) {
        return of(hit.value as T);
      }

      const pending = this.inFlight.get(key) as Observable<T> | undefined;
      if (pending) {
        return pending;
      }

      const request$ = factory().pipe(
        tap(value => this.set(key, value, ttlMs)),
        finalize(() => this.inFlight.delete(key)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      this.inFlight.set(key, request$);
      return request$;
    });
  }

  invalidate(keyPrefix: string): void {
    for (const key of [...this.entries.keys()]) {
      if (key.startsWith(keyPrefix)) {
        this.entries.delete(key);
      }
    }
  }

  clear(): void {
    this.entries.clear();
  }

  private set(key: string, value: unknown, ttlMs: number): void {
    this.entries.delete(key);
    this.entries.set(key, { value, expiresAt: Date.now() + ttlMs });
    while (this.entries.size > RequestCacheService.MAX_ENTRIES) {
      const oldest = this.entries.keys().next().value as string;
      this.entries.delete(oldest);
    }
  }
}
